/**
 * Question-attachment upload helpers.
 *
 * A prompt that tells the patient to "attach the documentation" used to render
 * as a text box and nothing else — there was nowhere to put the document
 * (case imp_adc19063, Michael, 2026-09-18). The questionnaire now shows an
 * upload slot under any question the backend flagged `attachment: true`, and
 * this module sends what the patient picks.
 *
 * It is the letter/card path with a third `doc_kind`, not new plumbing:
 *   1. the same downscale + byte-read pipeline (`prepareFileForUpload`),
 *   2. the same sealed-box encryption under the Mac's public key,
 *   3. the same POST to /api/bridge/assignments/{aid}/letter-upload,
 *   4. the same 600 KB bridge cap and 413 handling.
 *
 * The one addition is the tag that tells the physician WHICH question this
 * document answers. It rides INSIDE the sealed envelope — the bridge routes on
 * the outer `doc_kind` alone and never holds the question text, and bridge_sync
 * (which decrypts anyway) turns the tag into the caption on the Inbound queue
 * row.
 *
 * Patient UX contract: fire-and-forget. The upload never gates the
 * questionnaire — a patient whose photo fails to send can still answer, sign
 * and submit (their care team asks again if the document never arrives).
 */

import { sealSubmission, getBridgeSession } from "./bridgeClient";
import { prepareFileForUpload } from "./letterUpload";

const BRIDGE_BASE_URL = (import.meta.env.VITE_BRIDGE_URL || "").replace(/\/+$/, "");

/** doc_kind discriminator — must match bridge/routers/letters.py ALLOWED_DOC_KINDS. */
export const QUESTION_ATTACHMENT_DOC_KIND = "question_attachment";

interface BridgeSession {
  sessionToken?: string;
  assignmentId?: string;
  pubkeyB64?: string;
  [key: string]: unknown;
}

export interface QuestionAttachmentTag {
  /** The question's answer key (machine_name) the document belongs to. */
  questionTarget: string;
  /** The question's prompt, used as the caption the physician sees. */
  questionLabel?: string;
}

/**
 * Seal and upload one document the patient attached under a question.
 *
 * @param file    the raw file the patient selected (image or PDF)
 * @param tag     which question this document answers
 * @param sessionOverride  use this session instead of the stored one (tests)
 */
export async function sealAndUploadQuestionAttachment(
  file: File,
  tag: QuestionAttachmentTag,
  sessionOverride?: BridgeSession | null,
): Promise<{ letter_id: string; received_at: string }> {
  // `any` after the manual guard below: the session comes from getBridgeSession()
  // or an override, and is guarded by hand for the three keys used.
  const session: any = sessionOverride || getBridgeSession();
  if (!session?.sessionToken || !session?.assignmentId || !session?.pubkeyB64) {
    throw new Error("No active bridge session — cannot upload this attachment");
  }
  if (!tag?.questionTarget) {
    throw new Error("No question target — cannot file this attachment");
  }

  const { bytes, mime, filename } = await prepareFileForUpload(file);

  // Sealed envelope: the letter envelope plus the question tag. bridge_sync
  // reads `__letter__` to recognise a binary upload and `doc_kind` to route it.
  const payload = {
    __letter__: true,
    doc_kind: QUESTION_ATTACHMENT_DOC_KIND,
    b64: bytesToBase64(bytes),
    mime,
    filename,
    question_target: tag.questionTarget,
    question_label: tag.questionLabel || "",
  };

  const ciphertextB64 = sealSubmission(payload, session.pubkeyB64);

  const resp = await fetch(
    `${BRIDGE_BASE_URL}/api/bridge/assignments/${encodeURIComponent(session.assignmentId)}/letter-upload`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Patient-Token": session.sessionToken,
      },
      credentials: "omit",
      body: JSON.stringify({
        file_b64: ciphertextB64,
        mime,
        original_filename: filename,
        // Routing only. The question tag stays sealed.
        doc_kind: QUESTION_ATTACHMENT_DOC_KIND,
      }),
    },
  );

  if (!resp.ok) {
    const body = await resp.text();
    if (resp.status === 413) {
      throw new Error(
        "That file is too large to send. Try a lower-resolution photo, or one page at a time."
      );
    }
    throw new Error(`Upload failed (${resp.status}): ${body.slice(0, 200)}`);
  }
  return resp.json();
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}
